import IconButton from '@mui/material/IconButton'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import Tooltip from '@mui/material/Tooltip'
import FavoriteIcon from '@mui/icons-material/Favorite'
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder'

import { useAppState } from '../../reduxish/appState'

interface IProps {
    variant?: 'pill' | 'icon'
}

// Toggle between viewing all casinos and just the user's favorites. Two
// presentations: 'pill' (default segmented control for desktop) and 'icon' (a
// single heart IconButton for tight mobile toolbars). Disabled with a tooltip
// for anonymous users so the feature stays discoverable. Selection persists via
// HOME_FAVORITES_ONLY_SET (localStorage).
export default function HomeFavoritesToggle({ variant = 'pill' }: IProps) {
    const { state, dispatch } = useAppState()
    const isAuthenticated = state.auth?.isAuthenticated ?? false
    const favoritesOnly = isAuthenticated && state.homeFavoritesOnly

    const handleToggle = () => {
        if (!isAuthenticated) return
        dispatch({ type: 'HOME_FAVORITES_ONLY_SET', value: !state.homeFavoritesOnly })
    }

    const disabledTooltip = 'Log in to save favorite casinos'

    if (variant === 'icon') {
        const label = favoritesOnly ? 'show all casinos' : 'show favorites only'
        const tooltip = !isAuthenticated
            ? disabledTooltip
            : (favoritesOnly ? 'Showing favorites — tap to show all' : 'Show favorites only')
        return (
            <Tooltip title={tooltip}>
                <span style={{ display: 'inline-flex' }}>
                    <IconButton
                        aria-label={label}
                        aria-pressed={favoritesOnly}
                        onClick={handleToggle}
                        disabled={!isAuthenticated}
                        size="small"
                        sx={{ p: '10px' }}
                    >
                        {favoritesOnly
                            ? <FavoriteIcon sx={{ color: 'error.main' }} />
                            : <FavoriteBorderIcon />}
                    </IconButton>
                </span>
            </Tooltip>
        )
    }

    const value = favoritesOnly ? 'favorites' : 'all'
    const group = (
        <ToggleButtonGroup
            size="small"
            exclusive
            value={value}
            onChange={(_, next) => {
                if (next == null || !isAuthenticated) return
                dispatch({ type: 'HOME_FAVORITES_ONLY_SET', value: next === 'favorites' })
            }}
            aria-label="casino list filter"
            sx={{
                borderRadius: 999,
                backgroundColor: 'action.hover',
                p: 0.25,
                opacity: isAuthenticated ? 1 : 0.6,
                '& .MuiToggleButtonGroup-grouped': {
                    border: 0,
                    borderRadius: '999px !important',
                    px: 1.5,
                    py: 0.25,
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    textTransform: 'none',
                    color: 'text.secondary',
                    lineHeight: 1.4,
                    transition: 'background-color 120ms ease, color 120ms ease',
                    '&.Mui-selected': {
                        backgroundColor: 'background.paper',
                        color: 'text.primary',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: 'background.paper',
                        },
                    },
                    '&:hover': {
                        backgroundColor: 'transparent',
                    },
                },
            }}
        >
            <ToggleButton value="all" aria-label="all casinos" disabled={!isAuthenticated}>
                All
            </ToggleButton>
            <ToggleButton value="favorites" aria-label="favorites only" disabled={!isAuthenticated}>
                {value === 'favorites'
                    ? <FavoriteIcon fontSize="inherit" sx={{ mr: 0.5, color: 'error.main' }} />
                    : <FavoriteBorderIcon fontSize="inherit" sx={{ mr: 0.5 }} />}
                Favorites
            </ToggleButton>
        </ToggleButtonGroup>
    )

    if (!isAuthenticated) {
        return (
            <Tooltip title={disabledTooltip}>
                <span style={{ display: 'inline-flex' }}>{group}</span>
            </Tooltip>
        )
    }
    return group
}


